import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FullResultCard } from "../components/ResultCardPreview";

// Portal print page — student downloads result in school's chosen template format.
// URL: /portal-print/:examId?token=<portal_jwt>
// Reads token from query string (not localStorage — portal tokens are short-lived state).

const PRINT_STYLES = `
  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .print-controls { display: none !important; }
    .print-area { display: block !important; padding: 0 !important; margin: 0 !important; }
    .print-card { box-shadow: none !important; page-break-after: always; break-after: page; page-break-inside: avoid; }
    .print-card:last-child { page-break-after: avoid; break-after: avoid; }
  }
  @media screen {
    body { background: #e5e7eb; min-height: 100vh; font-family: sans-serif; }
    .print-controls {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      background: #1E3A5F; color: #fff;
      display: flex; align-items: center; gap: 16px;
      padding: 10px 24px; box-shadow: 0 2px 8px rgba(0,0,0,.3);
    }
    .print-controls h2 { margin: 0; font-size: 16px; flex: 1; }
    .print-btn {
      background: #F59E0B; color: #000; border: none; cursor: pointer;
      font-weight: 700; font-size: 14px; padding: 8px 20px; border-radius: 8px;
    }
    .print-btn:hover { background: #FBBF24; }
    .close-btn {
      background: rgba(255,255,255,.15); color: #fff; border: none; cursor: pointer;
      font-size: 14px; padding: 8px 16px; border-radius: 8px;
    }
    .print-area { padding: 80px 0 40px; display: flex; flex-direction: column; align-items: center; gap: 24px; }
    .print-card { background: #fff; box-shadow: 0 4px 24px rgba(0,0,0,.12); width: 794px; }
  }
`;

export default function PrintPortalResultCard() {
  const { examId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const root = document.getElementById("root");
    if (root) root.classList.add("print-target");
    return () => { if (root) root.classList.remove("print-target"); };
  }, []);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) { setError("Missing authentication token. Please close this window and try again."); return; }

    fetch(`/api/portal/print-data/${examId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Server returned ${r.status}`);
        return r.json();
      })
      .then((d) => setData(d))
      .catch((e) => setError(e.message));
  }, [examId]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: "sans-serif" }}>
        <h2 style={{ color: "#DC2626" }}>Failed to load result card</h2>
        <p style={{ color: "#555" }}>{error}</p>
        <button onClick={() => window.close()} style={{ marginTop: 16, padding: "8px 20px", cursor: "pointer" }}>Close</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: "sans-serif" }}>
        <p style={{ color: "#555", fontSize: 18 }}>Loading result card…</p>
      </div>
    );
  }

  const { template, school, exam, student, result, remark, attendance } = data;
  const title = `${student?.name} — ${exam?.name}`;

  return (
    <>
      <style>{PRINT_STYLES}</style>

      <div className="print-controls">
        <h2>Result Card — {title}</h2>
        <button className="print-btn" onClick={() => window.print()}>Print / Save PDF</button>
        <button className="close-btn" onClick={() => window.close()}>✕ Close</button>
      </div>

      <div className="print-area">
        <div className="print-card">
          <FullResultCard tpl={template} cols={1} printData={{ school, student, exam, result, remark, attendance }} />
        </div>
      </div>
    </>
  );
}
